import { Position, ScrollInfo } from 'codemirror';
import { Nullable } from 'frontend/types';
import { EditorView } from 'frontend/types/lessons/editor';

/**
 * STATE DEFINITIONS
 * There are two types of trackable state within editor views: code panels (scroll position and
 * code fold points) and the assets panel (selected asset and per-asset code state)
 */

/** State for code sections that should be persistent when switching between editor views */
export interface CodePanelEditorState {
  scrollInfo: Nullable<ScrollInfo>;
  codeFoldPoints: Position[];
}

/** Generate a default code panel state object */
export function getCodePanelDefaultState(): CodePanelEditorState {
  return { scrollInfo: null, codeFoldPoints: [] };
}

/**
 * State for the assets panel that should be persistent when switching to other views or when
 * switching between assets
 */
export interface AssetsPanelEditorState {
  selectedAssetIndex: Nullable<number>;
  assetsState: {
    [key: number]: CodePanelEditorState;
  };
}

/** The shape of _all_ editor state from Vuex's point of view */
export interface EditorState {
  camperdownState: CodePanelEditorState;
  assetsState: AssetsPanelEditorState;
  compiledElmState: CodePanelEditorState;
}

/**
 * STATE CHANGES
 * Use change objects to define a change to an aspect of the editor's state
 */

/** The base interface for state changes */
interface BaseEditorStateChange {
  type: string;
}

/** For tracking scroll position changes */
interface EditorScrollChange extends BaseEditorStateChange {
  type: 'scroll';
  scrollInfo: ScrollInfo;
}

/** For adding/removing code fold points */
export interface EditorFoldChange extends BaseEditorStateChange {
  type: 'fold';
  position: Position;
  addFold: boolean;
}

/** For adjusting code fold point locations when adding and removing lines around collapsed sections */
export interface EditorFoldAdjustment extends BaseEditorStateChange {
  type: 'fold-adjust';
  position: Position;
  lineDiff: number;
}

/**
 * Code panel state changes are a union of scroll changes, fold changes, and fold adjustments
 * (moving fold points when code is edited)
 */
export type CodePanelStateChange = EditorScrollChange | EditorFoldChange | EditorFoldAdjustment;

/** For changing the current asset index */
interface AssetIndexChange extends BaseEditorStateChange {
  type: 'asset-index';
  assetIndex: number;
}

/** For changing the code state of the current asset's code editor */
interface AssetCodeChange extends BaseEditorStateChange {
  type: 'asset-code';
  codeChange: CodePanelStateChange;
}

/** For deleting the code state of an asset that was deleted */
interface AssetDeletedChange extends BaseEditorStateChange {
  type: 'asset-delete';
  assetIndex: number;
}

/**
 * An asset panel change is the union of asset index changes and changes to the current
 * asset's code state
 */
export type AssetsPanelStateChange = AssetIndexChange | AssetCodeChange | AssetDeletedChange;

/**
 * STATE ACTIONS
 * Use state actions to update an aspect of the editor's state
 */

/**
 * This is the base interface for all state change actions. Each action consists of an action
 * type and a change object (something descended from `BaseEditorChange`). Each main view (beside
 * settings) has its own change action.
 */
interface BaseEditorStateAction {
  type: EditorView;
  change: BaseEditorStateChange;
}

/** For tracking updates to the Camperdown view */
interface CamperdownEditorStateAction extends BaseEditorStateAction {
  type: 'camperdown';
  change: CodePanelStateChange;
}

/** For tracking updates to the Compiled Elm view */
interface CompiledElmEditorStateAction extends BaseEditorStateAction {
  type: 'compiledElmMain';
  change: CodePanelStateChange;
}

/** For tracking updates to the Assets view */
interface AssetEditorStateAction extends BaseEditorStateAction {
  type: 'assets';
  change: AssetsPanelStateChange;
}

/**
 * A state action is the union of camperdown, compiled elm, and asset view actions.
 * Use state actions to send updated state to Vuex.
 */
export type EditorStateAction =
  | CamperdownEditorStateAction
  | CompiledElmEditorStateAction
  | AssetEditorStateAction;
